/** Wavetable definitions — predefined waveforms and helpers for PeriodicWave creation. */

/** Unique identifier for a built-in or custom wavetable. */
export type WavetableId =
	| "mellow-saw"
	| "electric-piano"
	| "organ"
	| "violin"
	| "pulse-25"
	| "custom";

/** A single wavetable definition with Fourier coefficients. */
export type WavetableDef = {
	readonly id: WavetableId;
	readonly label: string;
	/** Cosine harmonic amplitudes (real[0] = DC, real[1] = fundamental, ...). */
	readonly real: readonly number[];
	/** Sine harmonic amplitudes (imag[0] = 0, imag[1] = fundamental, ...). */
	readonly imag: readonly number[];
};

/** The five built-in wavetables. */
export const DEFAULT_WAVETABLES: readonly WavetableDef[] = [
	{
		id: "mellow-saw",
		label: "Mellow Saw",
		// Sawtooth with high harmonics rolled off for a warmer tone
		real: [0, 0, 0, 0, 0, 0, 0, 0, 0],
		imag: [0, 1.0, 0.5, 0.33, 0.22, 0.15, 0.1, 0.07, 0.04],
	},
	{
		id: "electric-piano",
		label: "E. Piano",
		// Rhodes-like: 4th partial prominent, inharmonic flavour
		real: [0, 0, 0, 0, 0, 0, 0, 0, 0],
		imag: [0, 1.0, 0.5, 0.2, 0.8, 0.3, 0.1, 0.2, 0.05],
	},
	{
		id: "organ",
		label: "Organ",
		// Hammond B3-style: 1st, 2nd, 4th, and 8th drawbar harmonics
		real: [0, 0, 0, 0, 0, 0, 0, 0, 0],
		imag: [0, 1.0, 0.7, 0.0, 0.6, 0.0, 0.0, 0.0, 0.5],
	},
	{
		id: "violin",
		label: "Violin",
		// Bowed string: strong odd and even harmonics with a bright edge
		real: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
		imag: [0, 1.0, 0.6, 0.8, 0.4, 0.5, 0.3, 0.4, 0.2, 0.3, 0.1],
	},
	{
		id: "pulse-25",
		label: "Pulse 25%",
		// 25% duty-cycle pulse — brighter, thinner sound than square
		real: [0, 0, 0, 0, 0, 0, 0, 0],
		imag: [0, 0.45, 0.32, 0.15, 0.0, -0.09, -0.05, -0.04],
	},
] as const;

/** Return a wavetable definition by its id, or undefined if not found. */
export function getWavetableDef(id: WavetableId): WavetableDef | undefined {
	return DEFAULT_WAVETABLES.find((w) => w.id === id);
}

/** Create a PeriodicWave from Fourier coefficient arrays. */
export function periodicWaveFromCoefficients(
	ctx: AudioContext,
	real: readonly number[],
	imag: readonly number[],
): PeriodicWave {
	return ctx.createPeriodicWave(
		new Float32Array(real),
		new Float32Array(imag),
		{ disableNormalization: false },
	);
}

/** Create a PeriodicWave from a built-in wavetable id. */
export function periodicWaveFromId(
	ctx: AudioContext,
	id: WavetableId,
): PeriodicWave | null {
	const def = getWavetableDef(id);
	if (!def) return null;
	return periodicWaveFromCoefficients(ctx, def.real, def.imag);
}

/**
 * Derive PeriodicWave coefficients from a raw PCM sample array.
 *
 * The algorithm picks the first power-of-two block from the samples,
 * runs a real-valued DFT, and returns the resulting real/imag arrays
 * (limited to the first 256 harmonics to stay within browser limits).
 */
export function periodicWaveFromSamples(
	ctx: AudioContext,
	samples: readonly number[],
): PeriodicWave {
	const len = samples.length;
	// Find largest power of two ≤ len (minimum 64)
	let n = 64;
	while (n * 2 <= len) n *= 2;

	const realCoeffs = new Float32Array(n / 2 + 1);
	const imagCoeffs = new Float32Array(n / 2 + 1);
	realCoeffs[0] = 0; // no DC
	imagCoeffs[0] = 0;

	// DFT (O(n²) — fine for n ≤ 4096)
	const limit = Math.min(n / 2 + 1, 257); // max 256 harmonics
	for (let k = 1; k < limit; k++) {
		let re = 0;
		let im = 0;
		for (let t = 0; t < n; t++) {
			const angle = (2 * Math.PI * k * t) / n;
			re += (samples[t] ?? 0) * Math.cos(angle);
			im -= (samples[t] ?? 0) * Math.sin(angle);
		}
		realCoeffs[k] = re / n;
		imagCoeffs[k] = im / n;
	}

	return ctx.createPeriodicWave(realCoeffs, imagCoeffs, {
		disableNormalization: false,
	});
}
